/**
 * The third of the three sheets the public screens carry: one A-sheet with two drawings on
 * it — the section `REZ A–A` on the left and the street elevation on the right.
 *
 * **What the sheet is.** A four-storey residential block with a basement and an attic, cut
 * through the staircase: foundations, floor slabs, cut walls in poché, the lift shaft, five
 * flights of dog-leg stair, the level datum staff, two vertical dimension chains and two
 * horizontal ones. Beside it the same building seen from the street — six bays by five
 * storeys, balconies, the entrance under its canopy, downpipes, cornice and mansard. Two
 * marks are the product rather than the building: the version cloud and the comment pin.
 *
 * **Every coordinate is computed once, at module scope.** 1 unit = 100 mm and the storey
 * module is 31.5 units, so the section and the elevation are both generated from one list of
 * level heights and one list of bay centres rather than pasted six times: the storey loop is
 * what keeps the two drawings on the same levels, which is the whole reason they are on one
 * sheet. The arithmetic is deterministic, there is nothing to recompute per render and
 * nothing random, so the same sheet is drawn on every reload.
 *
 * **The annotations are catalog messages**, not literals baked into the geometry — the
 * landing page has an EN/SK/DE switcher in its own footer, and a section labelled `PODKROVIE`
 * on an English page is the one element of the screen that did not get translated. Numbers do
 * not go through the catalog: `lib/i18n/formats.ts` has no number helper, and a drawing keeps
 * the continental spelling (`±0,000`, `3150`, `1:100`) in every language. Neither do
 * identifiers — `RES-B-A301`, `r03`, `SO 01`, `DSP`, the axis numbers.
 *
 * **Every colour is a token applied through the stylesheet** (`scripts/guard.mjs` fails the
 * build on a hex anywhere in `src/`), and the sheet paints no ground of its own: see the
 * `ground` prop and the stylesheet header.
 *
 * `aria-hidden`: it is the picture on the wall, not information.
 */

import { useId } from 'react';

import { useMessages } from '@/lib/i18n';
import type { MessageKeys } from '@/lib/i18n';

import styles from './SectionSheetDrawing.module.css';

/* ------------------------------------------------------------------ line families */

type Ink =
  | 'sheetBehind'
  | 'paper'
  | 'frame'
  | 'cut'
  | 'med'
  | 'thin'
  | 'hair'
  | 'hairLight'
  | 'lead'
  | 'axisLine'
  | 'cutLine'
  | 'solid'
  | 'cutFilled'
  | 'medFilled'
  | 'noStroke'
  | 'rev'
  | 'pin'
  | 'levelText'
  | 'axisText'
  | 'dimText'
  | 'storeyLabel'
  | 'liftLabel'
  | 'noteLabel'
  | 'drawingTitle'
  | 'titleScale'
  | 'tableHead'
  | 'tableValue'
  | 'tbKey'
  | 'tbProject'
  | 'tbValue'
  | 'tbMono'
  | 'wordmark'
  | 'revText'
  | 'pinText';

const CLASSES: Readonly<Record<Ink, string | undefined>> = {
  sheetBehind: styles.sheetBehind,
  paper: styles.paper,
  frame: styles.frame,
  cut: styles.cut,
  med: styles.med,
  thin: styles.thin,
  hair: styles.hair,
  hairLight: styles.hairLight,
  lead: styles.lead,
  axisLine: styles.axisLine,
  cutLine: styles.cutLine,
  solid: styles.solid,
  cutFilled: styles.cutFilled,
  medFilled: styles.medFilled,
  noStroke: styles.noStroke,
  rev: styles.rev,
  pin: styles.pin,
  levelText: styles.levelText,
  axisText: styles.axisText,
  dimText: styles.dimText,
  storeyLabel: styles.storeyLabel,
  liftLabel: styles.liftLabel,
  noteLabel: styles.noteLabel,
  drawingTitle: styles.drawingTitle,
  titleScale: styles.titleScale,
  tableHead: styles.tableHead,
  tableValue: styles.tableValue,
  tbKey: styles.tbKey,
  tbProject: styles.tbProject,
  tbValue: styles.tbValue,
  tbMono: styles.tbMono,
  wordmark: styles.wordmark,
  revText: styles.revText,
  pinText: styles.pinText,
};

const ink = (...names: readonly Ink[]) =>
  names
    .map((name) => CLASSES[name])
    .filter((name): name is string => name !== undefined)
    .join(' ');

/* ------------------------------------------------------------------ the shape list */

type Message = MessageKeys['drawing'];
/** The four poché hatches. Which one a cut shape carries is what it is made of. */
type Poche = 'masonry' | 'concrete' | 'roof' | 'earth';
/** The five things drawn once in `<defs>` and placed by `<use>`. */
type SymbolId = 'lvltri' | 'tick' | 'ewin' | 'rwin' | 'balc';

type Shape =
  | {
      readonly kind: 'rect';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly poche: Poche | null;
      /** Only the sheet behind this one uses it, and only to sit a degree and a half askew. */
      readonly turn: number;
    }
  /** The front sheet: its edge treatment depends on what it is lying on. See `ground`. */
  | {
      readonly kind: 'sheet';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
    }
  | {
      readonly kind: 'line';
      readonly cls: string;
      readonly x1: number;
      readonly y1: number;
      readonly x2: number;
      readonly y2: number;
    }
  | {
      readonly kind: 'path';
      readonly cls: string;
      readonly d: string;
      readonly poche: Poche | null;
    }
  | { readonly kind: 'poly'; readonly cls: string; readonly pts: string }
  | {
      readonly kind: 'circle';
      readonly cls: string;
      readonly cx: number;
      readonly cy: number;
      readonly r: number;
    }
  /** A number, an identifier or a symbol: never translated, see the module header. */
  | {
      readonly kind: 'glyph';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly text: string;
      readonly rotate: boolean;
    }
  | {
      readonly kind: 'label';
      readonly cls: string;
      readonly x: number;
      readonly y: number;
      readonly message: Message;
      readonly rotate: boolean;
    }
  | {
      readonly kind: 'use';
      readonly symbol: SymbolId;
      readonly x: number;
      readonly y: number;
    };

let current: Shape[] = [];

/** Closes the section being drawn and starts the next one. Order on the sheet is order here. */
function section(): readonly Shape[] {
  const done = current;
  current = [];
  return done;
}

function rect(
  x: number,
  y: number,
  w: number,
  h: number,
  cls: string,
  poche: Poche | null = null,
  turn = 0,
) {
  current.push({ kind: 'rect', cls, x, y, w, h, poche, turn });
}

function line(x1: number, y1: number, x2: number, y2: number, cls: string) {
  current.push({ kind: 'line', cls, x1, y1, x2, y2 });
}

function path(d: string, cls: string, poche: Poche | null = null) {
  current.push({ kind: 'path', cls, d, poche });
}

function poly(pts: string, cls: string) {
  current.push({ kind: 'poly', cls, pts });
}

function circle(cx: number, cy: number, r: number, cls: string) {
  current.push({ kind: 'circle', cls, cx, cy, r });
}

function glyph(x: number, y: number, text: string, cls: string, rotate = false) {
  current.push({ kind: 'glyph', cls, x, y, text, rotate });
}

function label(x: number, y: number, message: Message, cls: string, rotate = false) {
  current.push({ kind: 'label', cls, x, y, message, rotate });
}

function place(symbol: SymbolId, x: number, y: number) {
  current.push({ kind: 'use', symbol, x, y });
}

/* ------------------------------------------------------------------ the module */

/**
 * The level heights, in sheet units. 1 unit = 100 mm and the storey module is 31.5 units =
 * 3150 mm; the figures beside them are the datum the drawing is annotated with and stay
 * literals. Everything on both drawings hangs off this list.
 */
const LEVELS: readonly { readonly y: number; readonly datum: string }[] = [
  { y: 233, datum: '−3,000' },
  { y: 203, datum: '±0,000' },
  { y: 171.5, datum: '+3,150' },
  { y: 140, datum: '+6,300' },
  { y: 108.5, datum: '+9,450' },
  { y: 77, datum: '+12,600' },
  { y: 44, datum: '+15,900' },
];

/** The floor levels of the four habitable storeys — the loop both drawings are built from. */
const STOREYS = [203, 171.5, 140, 108.5] as const;
/** Grid axes 1–5: 3000/4500/4500/3000 mm. */
const AXES: readonly (readonly [string, number])[] = [
  ['1', 44],
  ['2', 74],
  ['3', 119],
  ['4', 164],
  ['5', 194],
];

/** The faces of the section's external and internal walls. */
const WEST = 41.5;
const EAST = 191.5;
const SPINE = 72.5;
const GROUND = 203;
const TERRAIN = 209;

/* ------------------------------------------------------------------ the sheets */

// The sheet behind this one, turned a degree and a half off square — which is the whole of
// why it reads as a second sheet rather than as a stray rule.
rect(16, 3, 380, 290, ink('sheetBehind'), null, -1.6);
const BEHIND = section();

current.push({ kind: 'sheet', x: 8, y: 6, w: 384, h: 288 });
rect(14, 12, 372, 276, ink('frame'));
// Trim marks: the four register ticks a sheet is cut to.
line(200, 8, 200, 12, ink('thin'));
line(200, 288, 200, 292, ink('thin'));
line(10, 150, 14, 150, ink('thin'));
line(386, 150, 390, 150, ink('thin'));
const SHEET = section();

/* ------------------------------------------------------------------ the grid */

for (const [name, x] of AXES) {
  line(x, 34.6, x, 260, ink('axisLine'));
  circle(x, 30, 4.6, ink('med'));
  glyph(x, 31.5, name, ink('axisText'));
}
const GRID = section();

/* ------------------------------------------------------------------ ground and structure */

// The terrain, stepping down to the basement wall on one side and flat on the other.
path('M14,209 H30 V228 H41.5 V250 H14 Z', ink('noStroke'), 'earth');
path('M196.5,209 H204 V250 H196.5 Z', ink('noStroke'), 'earth');
path('M14,209 H30 V228 H41.5', ink('med'));
line(196.5, TERRAIN, 204, TERRAIN, ink('med'));
const GROUNDWORKS = section();

// Foundations: strip footings under every wall, the lift pit, and the basement slab.
rect(37.5, 236, 13, 7, ink('cutFilled'), 'concrete');
rect(68.5, 236, 11, 7, ink('cutFilled'), 'concrete');
rect(187.5, 236, 13, 7, ink('cutFilled'), 'concrete');
rect(142, 245, 27.5, 5, ink('cutFilled'), 'concrete');
rect(146, 241, 19.5, 4, ink('cutFilled'), 'concrete');
rect(WEST, 233, 107.5, 3, ink('cutFilled'), 'concrete');
rect(162.5, 233, 34, 3, ink('cutFilled'), 'concrete');
rect(WEST, GROUND, 5, 16, ink('cutFilled'), 'concrete');
rect(WEST, 227, 5, 10, ink('cutFilled'), 'concrete');
rect(EAST, GROUND, 5, 34, ink('cutFilled'), 'concrete');
rect(SPINE, GROUND, 3, 12, ink('cutFilled'), 'concrete');
rect(SPINE, 233, 3, 4, ink('cutFilled'), 'concrete');
// The basement window into the light well.
line(43.2, 219, 43.2, 227, ink('med'));
line(44.8, 219, 44.8, 227, ink('med'));
line(40.1, 227, 46.5, 227, ink('med'));
const FOUNDATIONS = section();

// The lift: the shaft walls, the machine slab over it, the car, and a landing door per storey.
rect(146, 62, 3, 179, ink('cutFilled'), 'concrete');
rect(162.5, 62, 3, 25.5, ink('cutFilled'), 'concrete');
for (const y of [140, 171.5, GROUND]) {
  rect(162.5, y - 31.5, 3, 10.5, ink('cutFilled'), 'concrete');
}
rect(162.5, GROUND, 3, 38, ink('cutFilled'), 'concrete');
for (const y of STOREYS) {
  line(164, y - 21, 164, y, ink('med'));
  line(162.5, y - 21, 165.5, y - 21, ink('med'));
}
rect(145, 59, 22, 3, ink('cutFilled'), 'concrete');
rect(150.5, 182, 10.5, 21, ink('med'));
line(155.7, 182, 155.7, 63, ink('hair'));
label(157.5, 150, 'section.label.lift', ink('liftLabel'), true);
const LIFT = section();

/* ------------------------------------------------------------------ the cut walls */

/**
 * A wall column, cut: masonry everywhere except where an opening goes. The openings are
 * written as the storey's own reveal — head and sill relative to the floor level — so a
 * storey added to `STOREYS` gets its windows without a second list being edited.
 */
function wallColumn(
  x: number,
  width: number,
  top: number,
  bottom: number,
  holes: readonly (readonly [number, number])[],
) {
  const sorted = [...holes].sort((a, b) => a[0] - b[0]);
  let y = top;
  for (const [holeTop, holeBottom] of sorted) {
    if (holeTop > y) rect(x, y, width, holeTop - y, ink('cutFilled'), 'masonry');
    y = holeBottom;
  }
  if (bottom > y) rect(x, y, width, bottom - y, ink('cutFilled'), 'masonry');
}

/** The west wall's window sits 2400 mm above the floor and its sill 900 mm above it. */
const WEST_HOLES = STOREYS.map((y) => [y - 24, y - 9] as const);
/** The east wall's is a little lower and a little shorter — it faces the courtyard. */
const EAST_HOLES = STOREYS.map((y) => [y - 22, y - 9.5] as const);
/** The spine wall is pierced by a doorway per storey, from floor to lintel. */
const SPINE_HOLES = STOREYS.map((y) => [y - 21, y] as const);

wallColumn(WEST, 5, 77, GROUND, WEST_HOLES);
wallColumn(EAST, 5, 68, GROUND, EAST_HOLES);
wallColumn(SPINE, 3, 77, GROUND, SPINE_HOLES);
const WALLS = section();

// The reveals: every opening the walls above left, drawn as its jambs and its sill.
for (const [top, bottom] of WEST_HOLES) {
  line(43.2, top, 43.2, bottom, ink('med'));
  line(44.8, top, 44.8, bottom, ink('med'));
  line(40.1, bottom, 46.5, bottom, ink('med'));
}
for (const [top, bottom] of EAST_HOLES) {
  line(193.2, top, 193.2, bottom, ink('med'));
  line(194.8, top, 194.8, bottom, ink('med'));
  line(EAST, bottom, 197.9, bottom, ink('med'));
}
for (const [top, bottom] of SPINE_HOLES) {
  line(74, top, 74, bottom, ink('med'));
  line(SPINE, top, 75.5, top, ink('med'));
}
const REVEALS = section();

// The floor slabs, 300 mm, with the stair well left out of them.
for (const y of [GROUND, 171.5, 140, 108.5, 77]) {
  rect(WEST, y, 104.5, 3, ink('cutFilled'), 'concrete');
  rect(165.5, y, y === 77 ? 31 : 7, 3, ink('cutFilled'), 'concrete');
}
line(149, 75.6, EAST, 75.6, ink('thin'));
const SLABS = section();

/* ------------------------------------------------------------------ what is behind the cut */

/** One storey of seen-beyond fabric: floor finishes, two partitions and two doorways. */
function storeyInterior(y: number) {
  line(46.5, y + 3.6, SPINE, y + 3.6, ink('hairLight'));
  line(75.5, y + 3.6, 146, y + 3.6, ink('hairLight'));
  for (const x of [92, 131]) {
    line(x, y, x, y - 28.5, ink('hair'));
    rect(x + 3, y - 21, 9, 21, ink('hair'));
    line(x + 3.9, y - 21, x + 3.9, y, ink('hairLight'));
  }
  line(46.5, y - 0.6, SPINE, y - 0.6, ink('hairLight'));
  line(75.5, y - 0.6, 146, y - 0.6, ink('hairLight'));
}

for (const y of STOREYS) storeyInterior(y);
// The basement is not a flat: no floor finish over the slab, and the doors are stores.
for (const x of [92, 131]) {
  line(x, 233, x, 206, ink('hair'));
  rect(x + 3, 212, 9, 21, ink('hair'));
  line(x + 3.9, 212, x + 3.9, 233, ink('hairLight'));
}
line(46.5, 232.4, SPINE, 232.4, ink('hairLight'));
line(75.5, 232.4, 146, 232.4, ink('hairLight'));
const INTERIOR = section();

// The roof: a pitched build-up cut through, with the rafters behind it.
path('M36,79.8 L94,43 L146,76 L146,79.2 L94,46.2 L36,83 Z', ink('cutFilled'), 'roof');
line(94, 43, 94, 46.2, ink('cut'));
for (let i = 1; i <= 9; i += 1) {
  const x = 36 + i * 5.8;
  const y = 79.8 - (x - 36) * (36.8 / 58);
  line(x, y, x, y + 3.2, ink('hair'));
}
// One fewer on the right slope: it is shorter, and a rafter drawn past the eaves is a rafter
// hanging in the air.
for (let i = 1; i <= 8; i += 1) {
  const x = 94 + i * 5.8;
  const y = 43 + (x - 94) * (33 / 52);
  line(x, y, x, y + 3.2, ink('hair'));
}
line(EAST, 68, 196.5, 68, ink('cut'));
const ROOF = section();

/* ------------------------------------------------------------------ the stair */

/** One riser run, written as the zig-zag a stair is drawn as rather than as 2 × 9 lines. */
function flight(x: number, y: number, direction: 1 | -1, rise: number, going: number, steps: number) {
  let d = `M${String(x)},${String(y)}`;
  for (let i = 0; i < steps; i += 1) {
    d += ` v${String(-rise)} h${String(direction * going)}`;
  }
  path(d, ink('med'));
}

/** A storey of dog-leg stair: up to the half landing, turn, up to the floor above. */
function stairStorey(y: number, rise: number) {
  const half = y - 15.75;
  flight(172.5, y, 1, rise, 1.5, 9);
  line(172.5, y + 2.6, 186, y - 13.15, ink('thin'));
  rect(186, half, 5.5, 2.6, ink('medFilled'), 'concrete');
  flight(EAST, half, -1, rise, 1.5, 9);
  line(EAST, y - 13.15, 178, y - 28.9, ink('thin'));
  rect(172.5, y - 31.5, 5.5, 2.6, ink('medFilled'), 'concrete');
  line(173.5, y - 9, 185.5, y - 23, ink('hair'));
  line(190.5, y - 24.75, 178.5, y - 38.7, ink('hair'));
  line(173.5, y - 9, 173.5, y, ink('hair'));
  line(185.5, y - 23, 185.5, half, ink('hair'));
}

for (const y of STOREYS) stairStorey(y, 1.75);
// The basement flight rises 3000 rather than 3150, so its risers are shallower.
flight(172.5, 233, 1, 1.67, 1.5, 9);
line(172.5, 235.6, 186, 220.6, ink('thin'));
rect(186, 218, 5.5, 2.6, ink('medFilled'), 'concrete');
flight(EAST, 218, -1, 1.67, 1.5, 9);
line(EAST, 220.6, 178, 205.6, ink('thin'));
const STAIRS = section();

/* ------------------------------------------------------------------ the datum staff */

for (const [index, level] of LEVELS.entries()) {
  const runsTo = index === LEVELS.length - 1 ? 94 : WEST;
  line(17, level.y, runsTo, level.y, ink('lead'));
  place('lvltri', 24, level.y);
  glyph(41, level.y - 1.5, level.datum, ink('levelText'));
}
const DATUM = section();

/* ------------------------------------------------------------------ the chains */

// Storey to storey, then overall, then the two bay chains under the section.
line(210, 41, 210, 236, ink('thin'));
for (const level of LEVELS) {
  line(196.5, level.y, 213, level.y, ink('hair'));
  place('tick', 210, level.y);
}
const RISES: readonly (readonly [number, string])[] = [
  [218, '3000'],
  [187.25, '3150'],
  [155.75, '3150'],
  [124.25, '3150'],
  [92.75, '3150'],
  [60.5, '3300'],
];
for (const [y, figure] of RISES) glyph(208.4, y, figure, ink('dimText'), true);
line(219, 41, 219, 236, ink('thin'));
for (const y of [44, 233]) {
  line(211, y, 222, y, ink('hair'));
  place('tick', 219, y);
}
glyph(217.4, 138.5, '18900', ink('dimText'), true);

line(41, 256, 197, 256, ink('thin'));
for (const [, x] of AXES) place('tick', x, 256);
for (const [x, figure] of [
  [59, '3000'],
  [96.5, '4500'],
  [141.5, '4500'],
  [179, '3000'],
] as const) {
  glyph(x, 254.1, figure, ink('dimText'));
}
line(41, 264, 197, 264, ink('thin'));
for (const x of [44, 194]) place('tick', x, 264);
glyph(119, 262.1, '15000', ink('dimText'));
const CHAINS = section();

/* ------------------------------------------------------------------ the section's labels */

const STOREY_NAMES: readonly (readonly [number, number, Message])[] = [
  [50, 229.4, 'section.storey.basement'],
  [50, 199.4, 'section.storey.ground'],
  [50, 167.9, 'section.storey.first'],
  [50, 136.4, 'section.storey.second'],
  [50, 104.9, 'section.storey.third'],
  [64, 73.4, 'section.storey.attic'],
];
for (const [x, y, message] of STOREY_NAMES) label(x, y, message, ink('storeyLabel'));
label(17, 207.3, 'section.label.terrain', ink('noteLabel'));
label(84, 249, 'section.label.footing', ink('noteLabel'));
line(83, 247.8, 76.5, 243.6, ink('lead'));
label(44, 275, 'section.name', ink('drawingTitle'));
glyph(95, 275, 'M 1:100', ink('titleScale'));
line(44, 277.6, 69, 277.6, ink('med'));
const SECTION_LABELS = section();

/* ------------------------------------------------------------------ the elevation */

/** The six structural bays of the street facade, at their window jambs. */
const BAYS = [244, 266, 288, 310, 332, 354] as const;
/** The facade's own edges, its ground line and the top of its plinth. */
const FACE_W = 238;
const FACE_E = 372;
const PLINTH = 199;
/** Which bay the entrance takes out of the ground storey, and which two carry balconies. */
const ENTRANCE_BAY = 288;
const BALCONY_X = [260, 326] as const;

// The neighbours either side: outline only, filled by nothing. They are scenery.
path('M228,209 V132 H232 V116 H238', ink('med'));
path('M386,209 V142 H381 V128 H372', ink('med'));
line(232, 132, 232, TERRAIN, ink('hairLight'));
line(381, 142, 381, TERRAIN, ink('hairLight'));
line(228, TERRAIN, 386, TERRAIN, ink('med'));
line(228, 211.6, 386, 211.6, ink('hairLight'));

// The facade envelope, and the plinth it stands on. The plinth's joints are what stop the
// bottom of the elevation reading as an empty band.
path(`M${String(FACE_W)},209 V77 M${String(FACE_E)},209 V77`, ink('cut'));
line(FACE_W, TERRAIN, FACE_E, TERRAIN, ink('cut'));
line(FACE_W, PLINTH, FACE_E, PLINTH, ink('thin'));
for (let i = 1; i < 13; i += 1) {
  const x = FACE_W + (i * (FACE_E - FACE_W)) / 13;
  line(x, PLINTH, x, TERRAIN, ink('hairLight'));
}

// A string course at every floor level: the one line that turns six columns of windows into
// a facade with storeys.
for (const y of [171.5, 140, 108.5]) {
  line(FACE_W, y, FACE_E, y, ink('thin'));
  line(FACE_W, y + 1.2, FACE_E, y + 1.2, ink('hairLight'));
}

// The cornice and the mansard above it, with its tile courses.
rect(234, 74, 142, 3, ink('cut'));
line(236, 71.4, 374, 71.4, ink('thin'));
rect(234, 44, 142, 30, ink('med'));
for (let i = 1; i <= 5; i += 1) line(234, 44 + i * 5, 376, 44 + i * 5, ink('hairLight'));
line(232, 44, 378, 44, ink('cut'));
rect(268, 35, 9, 9, ink('cut'));
rect(265.5, 33, 14, 2.4, ink('cut'));

// Five storeys of windows on six bays. The top storey sits in the mansard, so it takes the
// roof window rather than the tall one; the ground storey gives one bay up to the entrance.
for (const x of BAYS) place('rwin', x - 0.5, 52);
for (const y of STOREYS) {
  for (const x of BAYS) {
    if (y === GROUND && x === ENTRANCE_BAY) continue;
    place('ewin', x, y - 24);
  }
}

// The entrance: the door in its reveal, the canopy over it, three steps down to the street.
rect(287, 182, 13, 21, ink('cut'));
rect(288.2, 183.2, 10.6, 18.6, ink('hair'));
line(293.5, 183.2, 293.5, 201.8, ink('hair'));
rect(283, 177.8, 21, 2.6, ink('med'));
line(283, 180.4, 304, 180.4, ink('hair'));
for (const [x, w, y] of [
  [284, 19, 203],
  [282, 23, 205],
  [280, 27, 207],
] as const) {
  rect(x, y, w, 2, ink('thin'));
}

// Balconies: two bays a storey, on the three storeys that have them.
for (const y of [171.5, 140, 108.5]) {
  for (const x of BALCONY_X) place('balc', x, y);
}

// The two downpipes, each with the shoe that throws the water clear of the plinth.
for (const x of [239.4, 369.4]) {
  line(x, 77, x, 206.5, ink('hair'));
  line(x + 1.2, 77, x + 1.2, 206.5, ink('hair'));
  line(x, 206.5, x - 1.6, 208.6, ink('hair'));
  line(x + 1.2, 206.5, x - 0.4, 208.6, ink('hair'));
}
const ELEVATION = section();

// The section line: where A–A is taken, marked on the elevation it is taken from.
line(302, 36, 302, 217, ink('cutLine'));
line(302, 36, 302, 45, ink('cut'));
line(302, 208, 302, 217, ink('cut'));
poly('302,45 302,49.5 297.6,47.25', ink('solid'));
poly('302,208 302,203.5 297.6,205.75', ink('solid'));
glyph(302, 34, 'A', ink('axisText'));
glyph(302, 223, 'A', ink('axisText'));
label(238, 222, 'elevation.name', ink('drawingTitle'));
line(238, 224.6, 283, 224.6, ink('med'));
const SECTION_LINE = section();

/* ------------------------------------------------------------------ the two marks */

/**
 * A version cloud: a rectangle walked clockwise with an outward arc per step. This is the one
 * mark on the sheet that is not the building — it is the product, drawn on the drawing.
 */
function cloud(x0: number, y0: number, x1: number, y1: number, step = 3.5) {
  const corners: readonly (readonly [number, number])[] = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
  let d = `M${String(x0)},${String(y0)}`;
  for (let i = 0; i < 4; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    if (a === undefined || b === undefined) continue;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const n = Math.max(2, Math.round(length / step));
    const r = Number(((length / n) * 0.72).toFixed(2));
    for (let j = 1; j <= n; j += 1) {
      const t = j / n;
      const px = Number((a[0] + (b[0] - a[0]) * t).toFixed(2));
      const py = Number((a[1] + (b[1] - a[1]) * t).toFixed(2));
      d += ` A${String(r)},${String(r)} 0 0 1 ${String(px)},${String(py)}`;
    }
  }
  path(`${d} Z`, ink('rev'));
}

cloud(305, 79, 326, 105);
poly('332,64 335.6,70.2 328.4,70.2', ink('rev'));
glyph(332, 69.4, '3', ink('revText'));
line(330, 70.6, 322, 78, ink('rev'));
circle(44, 123.5, 4, ink('pin'));
glyph(44, 124.8, '7', ink('pinText'));
const MARKS = section();

/* ------------------------------------------------------------------ the tables */

rect(200, 252, 44, 30, ink('med'));
for (const y of [259, 266, 273]) line(200, y, 244, y, ink('hair'));
line(212, 252, 212, 282, ink('hair'));
label(202, 257, 'sheet.rev.column.rev', ink('tableHead'));
label(214, 257, 'sheet.tb.date', ink('tableHead'));
for (const [index, entry] of (
  [
    ['r01', '03/2026'],
    ['r02', '06/2026'],
    ['r03', '09/2026'],
  ] as const
).entries()) {
  const [id, date] = entry;
  glyph(202, 264 + index * 7, id, ink('tableValue'));
  glyph(214, 264 + index * 7, date, ink('tableValue'));
}

// The title block. A micro-label and its value, never a colon: the colon is the one piece of
// punctuation that has to be re-typeset in every language for no gain.
const VALUE_X = 22;
rect(246, 228, 140, 54, ink('cut'));
for (const y of [240, 249, 258, 268]) line(246, y, 386, y, ink('med'));
for (const [y0, y1] of [
  [240, 249],
  [258, 268],
] as const) {
  line(322, y0, 322, y1, ink('med'));
}
line(300, 268, 300, 282, ink('med'));
label(249, 233.6, 'sheet.tb.project', ink('tbKey'));
label(249, 238.4, 'sheet.project', ink('tbProject'));
label(249, 246.4, 'sheet.tb.object', ink('tbKey'));
glyph(249 + VALUE_X, 246.4, 'SO 01', ink('tbValue'));
label(325, 246.4, 'sheet.tb.stage', ink('tbKey'));
glyph(325 + VALUE_X, 246.4, 'DSP', ink('tbValue'));
label(249, 255.4, 'sheet.tb.sheet', ink('tbKey'));
label(249 + VALUE_X, 255.4, 'section.sheetName', ink('tbValue'));
label(249, 265, 'sheet.tb.scale', ink('tbKey'));
glyph(249 + VALUE_X, 265, '1:100', ink('tbValue'));
label(325, 265, 'sheet.tb.date', ink('tbKey'));
glyph(325 + VALUE_X, 265, '09/2026', ink('tbValue'));
label(303, 274.4, 'sheet.tb.number', ink('tbKey'));
glyph(303 + VALUE_X + 4, 274.4, 'RES-B-A301', ink('tbMono'));
label(303, 280.4, 'sheet.tb.version', ink('tbKey'));
glyph(303 + VALUE_X + 4, 280.4, 'r03', ink('tbMono'));
// The wordmark is a proper noun and never translates.
glyph(250, 277.6, 'KALQA', ink('wordmark'));
const TABLES = section();

/* ------------------------------------------------------------------ the symbols */

/**
 * The five repeated things, drawn once in `<defs>` and placed by `<use>`. They are what makes
 * the elevation a facade rather than a rectangle: a window is six lines, and six lines pasted
 * twenty-nine times is a file nobody can change.
 */
function symbol(id: SymbolId, build: () => void): { readonly id: SymbolId; readonly shapes: readonly Shape[] } {
  build();
  return { id, shapes: section() };
}

const SYMBOLS: readonly { readonly id: SymbolId; readonly shapes: readonly Shape[] }[] = [
  // A level tag: the open triangle with its lower-left quadrant filled, which is the datum mark.
  symbol('lvltri', () => {
    poly('0,0 -3.3,-3.3 3.3,-3.3', ink('med'));
    poly('0,0 -3.3,-3.3 0,-3.3', ink('solid'));
  }),
  // The oblique tick a dimension chain is stationed with, rather than an arrowhead.
  symbol('tick', () => {
    line(-2, 2, 2, -2, ink('thin'));
  }),
  // A storey window seen in elevation: opening, reveal, mullion, transom, sill and its shadow.
  symbol('ewin', () => {
    rect(0, 0, 11, 15, ink('med'));
    rect(0.9, 0.9, 9.2, 13.2, ink('hair'));
    line(5.5, 0.9, 5.5, 14.1, ink('hair'));
    line(0.9, 5.5, 10.1, 5.5, ink('hair'));
    rect(-1, 15, 13, 1.2, ink('hair'));
    line(-1, 16.2, 12, 16.2, ink('hairLight'));
  }),
  // The same window in the mansard: shorter, and its sill is the tile course.
  symbol('rwin', () => {
    rect(0, 0, 12, 10, ink('med'));
    rect(1, 1, 10, 8, ink('hair'));
    line(6, 1, 6, 9, ink('hair'));
    line(1, 5, 11, 5, ink('hair'));
    rect(-0.8, 10, 13.6, 1, ink('hair'));
  }),
  // A balcony: the slab, two end posts, the handrail, the mid rail and twelve balusters.
  symbol('balc', () => {
    rect(0, 0, 23, 2, ink('med'));
    line(0.6, 0, 0.6, -11, ink('thin'));
    line(22.4, 0, 22.4, -11, ink('thin'));
    line(0, -11, 23, -11, ink('thin'));
    line(0, -5.5, 23, -5.5, ink('hair'));
    for (let i = 1; i <= 12; i += 1) line(0.5 + i * 1.7, -11, 0.5 + i * 1.7, 0, ink('hair'));
  }),
];

/* ------------------------------------------------------------------ the component */

const SECTIONS: readonly (readonly Shape[])[] = [
  BEHIND,
  SHEET,
  GRID,
  GROUNDWORKS,
  FOUNDATIONS,
  LIFT,
  WALLS,
  REVEALS,
  SLABS,
  INTERIOR,
  ROOF,
  STAIRS,
  DATUM,
  CHAINS,
  SECTION_LABELS,
  ELEVATION,
  SECTION_LINE,
  MARKS,
  TABLES,
];

/** Two decimals is the precision the geometry was authored at; more is noise in the markup. */
function round(n: number) {
  return Number(n.toFixed(2));
}

export interface SectionSheetDrawingProps {
  /**
   * How the sheet meets a frame that is not 4:3. `cover` fills it and crops the margins;
   * `contain` fits the whole sheet inside it. The datum staff and the title block live at the
   * edges, so a square plate asks for `contain`.
   */
  readonly fit?: 'cover' | 'contain';
  /**
   * What the sheet is lying on, which decides how its edge is drawn. On `paper` the plate is
   * white and the sheet is separated from it by a soft drop shadow. On `ink` that shadow is a
   * dark blur over a dark surface — invisible work — so it is dropped and the sheet keeps its
   * hairline trim edge instead, which is all the separation white paper on ink needs.
   */
  readonly ground?: 'paper' | 'ink';
}

function draw(
  shape: Shape,
  key: number,
  scope: string,
  ground: 'paper' | 'ink',
  t: (m: Message) => string,
) {
  switch (shape.kind) {
    case 'rect':
      return (
        <rect
          key={key}
          className={shape.cls}
          x={round(shape.x)}
          y={round(shape.y)}
          width={round(shape.w)}
          height={round(shape.h)}
          fill={shape.poche === null ? undefined : `url(#${scope}-${shape.poche})`}
          transform={shape.turn === 0 ? undefined : `rotate(${String(shape.turn)} 200 150)`}
        />
      );
    case 'sheet':
      return (
        <rect
          key={key}
          className={styles.paper}
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          filter={ground === 'ink' ? undefined : `url(#${scope}-shadow)`}
        />
      );
    case 'line':
      return (
        <line
          key={key}
          className={shape.cls}
          x1={round(shape.x1)}
          y1={round(shape.y1)}
          x2={round(shape.x2)}
          y2={round(shape.y2)}
        />
      );
    case 'path':
      return (
        <path
          key={key}
          className={shape.cls}
          d={shape.d}
          fill={shape.poche === null ? undefined : `url(#${scope}-${shape.poche})`}
        />
      );
    case 'poly':
      return <polygon key={key} className={shape.cls} points={shape.pts} />;
    case 'circle':
      return (
        <circle
          key={key}
          className={shape.cls}
          cx={round(shape.cx)}
          cy={round(shape.cy)}
          r={shape.r}
        />
      );
    case 'glyph':
    case 'label': {
      const x = round(shape.x);
      const y = round(shape.y);
      return (
        <text
          key={key}
          className={shape.cls}
          x={x}
          y={y}
          transform={shape.rotate ? `rotate(-90 ${String(x)} ${String(y)})` : undefined}
        >
          {shape.kind === 'glyph' ? shape.text : t(shape.message)}
        </text>
      );
    }
    case 'use':
      return (
        <use
          key={key}
          href={`#${scope}-${shape.symbol}`}
          x={round(shape.x)}
          y={round(shape.y)}
        />
      );
  }
}

export function SectionSheetDrawing({
  fit = 'cover',
  ground = 'paper',
}: SectionSheetDrawingProps) {
  const t = useMessages('drawing');
  /* Per-instance ids for the four poché patterns, the drop shadow and the five symbols. A page
     drawing this twice — and the landing and the entrance are one route apart — would
     otherwise have two elements sharing an `id`, which is invalid markup whose `#…` references
     all resolve to whichever copy the parser saw first. The colons React puts in `useId` are
     stripped: a fragment identifier carrying them is a URL reference nobody should have to
     reason about. */
  const scope = `kalqa-section-${useId().replace(/:/g, '')}`;

  return (
    <svg
      className={styles.view}
      viewBox="0 0 400 300"
      preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
      aria-hidden
    >
      <defs>
        <pattern
          id={`${scope}-masonry`}
          width="2.6"
          height="2.6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect className={styles.patternBg} x="0" y="0" width="2.6" height="2.6" />
          <line className={styles.hatch} x1="0" y1="0" x2="0" y2="2.6" />
        </pattern>
        <pattern id={`${scope}-concrete`} width="4" height="4" patternUnits="userSpaceOnUse">
          <rect className={styles.patternBg} x="0" y="0" width="4" height="4" />
          <circle className={styles.hatchDot} cx="1" cy="1.2" r="0.3" />
          <circle className={styles.hatchDot} cx="3.1" cy="2.9" r="0.3" />
          <line className={styles.hatch} x1="2.4" y1="0.5" x2="3.4" y2="1.5" />
        </pattern>
        <pattern
          id={`${scope}-roof`}
          width="3"
          height="3"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect className={styles.patternBg} x="0" y="0" width="3" height="3" />
          <line className={styles.hatch} x1="0" y1="0" x2="0" y2="3" />
          <line className={styles.hatch} x1="0" y1="0" x2="3" y2="0" />
        </pattern>
        <pattern
          id={`${scope}-earth`}
          width="3"
          height="3"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line className={styles.hatchEarth} x1="0" y1="0" x2="0" y2="3" />
        </pattern>
        <filter id={`${scope}-shadow`} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow className={styles.shadow} dx="1.4" dy="1.8" stdDeviation="1.8" />
        </filter>
        {SYMBOLS.map((entry) => (
          <g key={entry.id} id={`${scope}-${entry.id}`}>
            {entry.shapes.map((child, index) => draw(child, index, scope, ground, t))}
          </g>
        ))}
      </defs>
      {SECTIONS.map((shapes, group) => (
        <g key={group}>{shapes.map((child, index) => draw(child, index, scope, ground, t))}</g>
      ))}
    </svg>
  );
}
